import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import { connect, useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { chipUseStyle } from "styles/chip";

import { Chip, IconButton } from "@material-ui/core";
import CancelIcon from "@material-ui/icons/Cancel";

import useFetchUserDetailsHandler from "hooks/useFetchUserDetailsHandler";

import { addUser, editUser } from "api/users";

import { validateEmail } from "utils/utils.js";
import { InputErrorStrings, landingPageStrings, userProfileStrings, valueStrings } from "constants/constantStrings";

import Dropdown from "components/Dropdown";
import DupontButton from "components/DupontButton";
import DupontDialog from "components/DupontDialog";
import ErrorLabel from "components/ErrorLabel";
import InputBox from "components/InputBox";
import SelectDropDown from "components/SelectDropDown";

import "./index.scss";

const isAdminUser = role => role?.toLowerCase() === userProfileStrings.admin.toLowerCase();

function UserForm({ usersData, user, setUser, roles, systemsList, type, idToken, onUserFormClose }) {
  const chipClasses = chipUseStyle(this);
  const admin = isAdminUser(user.Role);
  const isEditForm = type === "Edit";

  const [userFormData, setUserFormData] = useState({});
  const [emailError, setEmailError] = useState("");
  const [userError, setUserError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [ssoIdError, setSsoIdError] = useState("");
  const [userRoleError, setUserRoleError] = useState(null);
  const [selectedSystems, setSelectedSystems] = useState(user.Systems);
  const [confirmDisable, setconfirmDisable] = useState(true);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isLoading, setisLoading] = useState(false);

  const currentUser = useSelector(state => state.user);

  const { fetchUserDetails } = useFetchUserDetailsHandler(useDispatch());
  useEffect(() => {
    const formErr = userError || emailError;
    validateForm({ ...user }, formErr);
  }, [userError, emailError]);

  const checkIfFieldExist = (field, value) => {
    const item = usersData.find(userData => userData[field] === value);
    return Boolean(item);
  };

  const validateForm = (userData, fieldError) => {
    const { Email, Name, Role, Mobile } = userData;
    const isFormFiledError = Boolean(fieldError || emailError || userError);
    let isInvalid = !(Name && Role && Mobile) || isFormFiledError;
    if (!isEditForm && !isInvalid) {
      isInvalid = !Email;
    }
    setconfirmDisable(isInvalid);
  };
  const handleUserChange = (value, name) => {
    if (name === "Name") {
      let nameError = "";
      const isNameValid = /^(?!\s)(?![\s\S]*\s$)[A-Za-z0-9@^$.!`#+'~_-\s]*$/.test(value);
      if (!value) nameError = InputErrorStrings.required;
      else if (!isNameValid) nameError = userProfileStrings.invalidUserName;
      else if (value.length > 100 || value.length < 3) nameError = userProfileStrings.userNameLength;
      setUserError(nameError);
    } else if (name === "Email") {
      let emailError = "";
      if (!value) emailError = InputErrorStrings.required;
      else if (!validateEmail(value)) emailError = userProfileStrings.invalidEmail;
      else if (checkIfFieldExist(name, value)) emailError = userProfileStrings.emailExist;
      setEmailError(emailError);
    } else if (name === "SsoId") {
      validateSSOid(value);
    }
    setUser({ ...user, [name]: value });
    setUserFormData({ ...userFormData, [name]: value });
  };

  const validateSSOid = value => {
    let errorMsg = "";
    const invalidSSOid = /[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(value);
    if (!value) {
      errorMsg = InputErrorStrings.required;
    } else if (invalidSSOid) {
      errorMsg = userProfileStrings.invalidSSOid;
    } else if (value.length > 18 || value.length < 18) {
      errorMsg = userProfileStrings.ssoidLength;
    } else if (checkIfFieldExist("SsoId", value)) {
      errorMsg = userProfileStrings.ssoidExist;
    }
    setSsoIdError(errorMsg);
    validateForm({ ...user, SsoId: value }, errorMsg);
  };

  const getSystemsIds = systemList => systemList.reduce((ids, sys) => [...ids, systemsList[sys]], []);

  const removeSystem = name => {
    let filteredSystem = [];
    if (name) {
      filteredSystem = selectedSystems?.filter(system => system !== name);
      setSelectedSystems(filteredSystem);
    } else {
      setSelectedSystems([]);
    }
    filteredSystem = getSystemsIds(filteredSystem);
    validateForm(user);
    setUser({ ...user, Systems: filteredSystem });
    setUserFormData({ ...userFormData, Systems: filteredSystem });
  };

  const handleSystemChange = value => {
    const allSelected = value?.includes(landingPageStrings.all.trim());
    if ((value.length > 0 || user.Role === userProfileStrings.tier1|| user.Role === userProfileStrings.tier2 ) && allSelected) {
      const Systems = selectAllSystems();
      setUser({ ...user, Systems });
      setUserFormData({ ...userFormData, Systems });
    } else {
      setSelectedSystems(value);
      setUser({ ...user, Systems: getSystemsIds(value) });
      setUserFormData({ ...userFormData, Systems: getSystemsIds(value) });
    }
    validateForm(user);
  };

  const selectAllSystems = () => {
    const allSystems = { ...systemsList };
    delete allSystems.All;
    setSelectedSystems(Object.keys(allSystems));
    return Object.values(allSystems);
  };

  const handleRoleChange = Role => {
    const error = !Role ? InputErrorStrings.required : null;
    setUserRoleError(error);
    let userData = { ...user };
    if (isAdminUser(Role)) {
      const Systems = selectAllSystems();
      userData = { ...user, Role, Systems };
      setUser(userData);
      setUserFormData({ ...userFormData, Role, Systems });
    } else {
      const Systems = [];
      userData = { ...user, Role, Systems };
      setUser(userData);
      setUserFormData({ ...userFormData, Role, Systems });
      setSelectedSystems(Systems);
    }
    validateForm(userData, error);
  };

  const handleStatusChange = event => {
    const { value } = event.target;
    const Status = Boolean(+value);
    const newUserData = { ...user, Status };
    validateForm(newUserData);
    setUser(newUserData);
    setUserFormData({ ...userFormData, Status });
  };

  const onMobileChange = Mobile => {
    const data = { ...userFormData, Mobile };
    validateForm({ ...user, Mobile });
    setUserFormData(data);
    setUser({ ...user, Mobile });
    let phoneError = "";
    if (!Mobile) phoneError = InputErrorStrings.required;
    setPhoneError(phoneError);
  };

  const isRemoveBtnDisabled = () => isAdminUser(user.Role) || !selectedSystems?.length;

  const submitNewUser = async () => {
    try {
      const newUserData = await addUser(user);
      setconfirmDisable(false);
      if (newUserData) {
        onUserFormClose(newUserData[0], true);
      }
    } catch (err) {
      setconfirmDisable(false);
      if (err.response.data.detail.includes("Email")) {
        setEmailError(userProfileStrings.emailExist);
      }
      if (err.response.data.detail.includes("SsoId")) {
        setSsoIdError(userProfileStrings.ssoidExist);
      }
    } finally {
      setIsSubmitClicked(true);
      setisLoading(false);
    }
  };

  const submitEditUser = async () => {
    try {
      const { UserId } = user;
      const payload = { UserId, ...userFormData };
      const userUpdatedData = await editUser(payload);
      if (userUpdatedData) {
        onUserFormClose(userUpdatedData[0]);
      }
      setisLoading(false);
      if (user.Email == currentUser.UserEmail) {
        fetchUserDetails();
      }
    } catch (err) {
      setconfirmDisable(false);
      setIsSubmitClicked(true);
      setisLoading(false);
    }
  };

  const getAddUserDlgTitle = () => (isEditForm ? userProfileStrings.editUser : userProfileStrings.addaUser);
  const getAddUserDlgSubTitle = () => (isEditForm ? userProfileStrings.editUserMessage : userProfileStrings.addUserMessage);
  const getConfirmBtnText = () => (isEditForm ? valueStrings.save : valueStrings.add);

  const onFormClose = action => {
    if (action === "CONFIRM") {
      setconfirmDisable(true);
      setisLoading(true);
      if (isEditForm) {
        submitEditUser();
      } else {
        submitNewUser();
      }
    } else {
      onUserFormClose();
    }
  };

  return (
    <div className="user-fomm__container">
      <DupontDialog
        open
        title={getAddUserDlgTitle()}
        subTitle={getAddUserDlgSubTitle()}
        showisRequired
        cancelBtnText={valueStrings.cancelBtnText}
        confirmBtnText={getConfirmBtnText()}
        onClose={onFormClose}
        confirmDisabled={confirmDisable}
        isOverlayLoader={isLoading}
      >
        <div className="column margin-top-10">
          <InputBox
            disabled={isEditForm}
            labelText={`${userProfileStrings.emailid}*`}
            customClass="form-group"
            name="Email"
            placeholder={valueStrings.type}
            value={user.Email}
            onChange={handleUserChange}
            errorMsg={emailError}
          />
          <InputBox
            disabled={isEditForm}
            customClass="form-group"
            labelText={`${userProfileStrings.ssoId}`}
            name="SsoId"
            placeholder={valueStrings.type}
            value={user.SsoId}
            maxLength={18}
            onChange={handleUserChange}
            errorMsg={ssoIdError}
          />
          <Dropdown
            customClass="form-group"
            disabled={idToken.sub === user.UserId}
            labelText={`${userProfileStrings.role}*`}
            value={user.Role}
            options={roles}
            inputValue={user.Role}
            errorMsg={userRoleError}
            onChange={handleRoleChange}
            placeholder={valueStrings.chooseOptionStr}
          />
          <SelectDropDown
            customClass="form-group"
            labelText={`${landingPageStrings.systems} ${admin ? "*" : ""}`}
            value={selectedSystems}
            onChange={handleSystemChange}
            options={Object.keys(systemsList)}
            disabled={admin}
          />
          {(selectedSystems.length === 0 && admin) ||
            (isSubmitClicked && !user.Role && <div className="errorText">{InputErrorStrings.required}</div>)}
        </div>

        <div className="column margin-top-10">
          <InputBox
            customClass="form-group"
            labelText={`${userProfileStrings.userName}*`}
            name="Name"
            placeholder={valueStrings.type}
            value={user.Name}
            onChange={handleUserChange}
            errorMsg={userError}
            maxLength={30}
          />
          <div className="bodyMainHeadingText mobile__label">
            {userProfileStrings.addNotificationMobile}*&nbsp;
            <span className="mobile-info">({userProfileStrings.prefixCountryCode})</span>
          </div>
          <div className="form-group">
            <PhoneInput
              placeholder={userProfileStrings.addNotificationMobilePlaceHolder}
              inputClass="mobile-input"
              value={user.Mobile}
              containerClass={phoneError ? "error-phone" : null}
              onChange={onMobileChange}
            />
            {phoneError && <ErrorLabel message={phoneError} />}
          </div>

          <div className="form-group">
            <label className="dupont-form-label">{userProfileStrings.status}*</label>
            <div className="d-flex marginTop10" onChange={handleStatusChange}>
              <label className="radiocontainer descriptionText">
                {userProfileStrings.active}
                <input className="radioLabel" type="radio" value="1" defaultChecked={user.Status} name="status" />
                <span className="  radioButton"> </span>
              </label>

              <label className="radiocontainer descriptionText">
                {userProfileStrings.inactive}
                <input className="radioLabel" type="radio" value="0" defaultChecked={!user.Status} name="status" />
                <span className="  radioButton"> </span>
              </label>
            </div>
          </div>

          <div className="flex-between">
            <label className="dupont-form-label" disabled={admin}>
              {userProfileStrings.selectedSystems} {admin ? "*" : ""}
            </label>
            <DupontButton type="tertiary" customClass="remove-all-systems" disabled={isRemoveBtnDisabled()} onClick={() => removeSystem()}>
              {userProfileStrings.removeAll}
            </DupontButton>
          </div>
          <div className="selecteduserSystemBackground descriptionText">
            {selectedSystems?.length > 0
              ? selectedSystems.map(name => (
                  <Chip
                    label={name}
                    disabled={admin}
                    key={name}
                    className={chipClasses.root}
                    onDelete={() => removeSystem(name)}
                    deleteIcon={
                      <IconButton disabled={admin}>
                        <CancelIcon />
                      </IconButton>
                    }
                  />
                ))
              : valueStrings.noSystemStr}
          </div>

          {isSubmitClicked && user.Status === "" ? <div className="errorText">{InputErrorStrings.required}</div> : null}
        </div>
      </DupontDialog>
    </div>
  );
}

const mapStateToProps = ({ auth }) => ({
  idToken: auth.idToken
});

export default connect(mapStateToProps)(UserForm);
